import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  User, Calendar, FileText, MessageCircle, BarChart3, Settings, 
  Bell, Search, Menu, X, ChevronRight, Clock, CheckCircle, 
  AlertCircle, Upload, Download, Eye, EyeOff, Star, TrendingUp,
  Briefcase, Users, MapPin, Phone, Mail, Globe, LinkedinIcon,
  Github, ExternalLink, Plus, Edit3, Save, Cancel, RefreshCw
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

// Context for candidate data
const CandidateContext = React.createContext();

const CandidatePortal = () => {
  const { candidateId } = useParams();
  const navigate = useNavigate();
  
  // Debug logging
  console.log('CandidatePortal component rendered!', { candidateId });
  
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [candidateData, setCandidateData] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  
  // Mock current candidate - in real app this would come from authentication
  const currentCandidate = {
    id: candidateId || 'candidate123',
    email: 'john.doe@email.com',
    tenant_id: 'tenant1'
  };

  useEffect(() => {
    fetchCandidateData();
    fetchDashboardData();
  }, [candidateId]);

  const fetchCandidateData = async () => {
    try {
      const response = await axios.get(`${API}/api/candidates/${currentCandidate.id}/profile`);
      setCandidateData(response.data.profile || {});
    } catch (error) {
      console.error('Error fetching candidate data:', error);
      // Mock data for development
      setCandidateData({
        id: currentCandidate.id,
        first_name: 'John',
        last_name: 'Doe',
        email: 'john.doe@email.com',
        phone: '+1 (555) 123-4567',
        location: 'San Francisco, CA',
        current_title: 'Senior Software Engineer',
        current_company: 'TechCorp Inc.',
        profile_completion_percentage: 85.5
      });
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/candidates/${currentCandidate.id}/dashboard`, {
        params: { tenant_id: currentCandidate.tenant_id }
      });
      setDashboardData(response.data.dashboard || {});
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Use mock data from service
      setDashboardData({
        profile_completion: 85.5,
        active_applications: [
          {
            job_title: 'Senior Software Engineer',
            company: 'TechCorp',
            current_stage: 'interviewing',
            progress_percentage: 60.0
          }
        ],
        upcoming_interviews: [
          {
            job_title: 'Senior Software Engineer', 
            interview_type: 'technical',
            scheduled_time: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(),
            interviewer_name: 'Sarah Johnson',
            location: 'Video Call'
          }
        ],
        pending_tasks: [
          {
            title: 'Complete pre-interview questionnaire',
            deadline: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
            priority: 'high'
          }
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  const navigation = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3, count: null },
    { id: 'profile', label: 'My Profile', icon: User, count: null },
    { id: 'applications', label: 'Applications', icon: Briefcase, count: dashboardData?.active_applications?.length || 0 },
    { id: 'interviews', label: 'Interviews', icon: Calendar, count: dashboardData?.upcoming_interviews?.length || 0 },
    { id: 'documents', label: 'Documents', icon: FileText, count: null },
    { id: 'messages', label: 'Messages', icon: MessageCircle, count: notifications.length },
    { id: 'feedback', label: 'Feedback', icon: Star, count: null },
    { id: 'settings', label: 'Settings', icon: Settings, count: null }
  ];

  const formatDateTime = (dateTimeString) => {
    const date = new Date(dateTimeString);
    return {
      date: date.toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric' 
      }),
      time: date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      })
    };
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return 'bg-green-500';
    if (percentage >= 60) return 'bg-blue-500';
    if (percentage >= 40) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-12 w-12 text-purple-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading your portal...</p>
        </div>
      </div>
    );
  }

  return (
    <CandidateContext.Provider value={{ candidateData, setCandidateData, dashboardData, fetchDashboardData }}>
      <div className="min-h-screen bg-gray-50 flex">
        {/* Mobile Sidebar Overlay */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside className={`
          fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 
          transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
            <h1 className="text-xl font-bold text-gray-900">Candidate Portal</h1>
            <button 
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-1 rounded-md hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Profile Summary */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center text-white font-semibold">
                {candidateData?.first_name?.[0]}{candidateData?.last_name?.[0]}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {candidateData?.first_name} {candidateData?.last_name}
                </p>
                <p className="text-xs text-gray-600 truncate">{candidateData?.current_title}</p>
              </div>
            </div>
            
            {/* Profile Completion */}
            <div className="mt-4">
              <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                <span>Profile Completion</span>
                <span>{candidateData?.profile_completion_percentage || 0}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full ${getProgressColor(candidateData?.profile_completion_percentage || 0)}`}
                  style={{ width: `${candidateData?.profile_completion_percentage || 0}%` }}
                />
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-4">
            {navigation.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  setActiveTab(item.id);
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center justify-between px-6 py-3 text-left text-sm font-medium transition-colors ${
                  activeTab === item.id
                    ? 'bg-purple-50 text-purple-700 border-r-2 border-purple-600'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <item.icon className="h-5 w-5" />
                  <span>{item.label}</span>
                </div>
                {item.count > 0 && (
                  <span className="bg-purple-600 text-white text-xs rounded-full px-2 py-1">
                    {item.count}
                  </span>
                )}
              </button>
            ))}
          </nav>

          {/* Quick Actions */}
          <div className="p-6 border-t border-gray-200">
            <button 
              onClick={() => navigate('/schedule')}
              className="w-full px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors"
            >
              Schedule Interview
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 flex flex-col lg:ml-0">
          {/* Top Header */}
          <header className="bg-white border-b border-gray-200 px-4 py-3 lg:px-6 lg:py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button 
                  onClick={() => setSidebarOpen(true)}
                  className="lg:hidden p-2 rounded-md hover:bg-gray-100"
                >
                  <Menu className="h-5 w-5" />
                </button>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 capitalize">
                    {activeTab === 'dashboard' ? `Welcome back, ${candidateData?.first_name}!` : activeTab}
                  </h2>
                  <p className="text-sm text-gray-600">
                    {activeTab === 'dashboard' 
                      ? `You have ${dashboardData?.pending_tasks?.length || 0} pending tasks` 
                      : `Manage your ${activeTab}`
                    }
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                {/* Notifications */}
                <button className="relative p-2 text-gray-600 hover:text-gray-900">
                  <Bell className="h-5 w-5" />
                  {notifications.length > 0 && (
                    <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {notifications.length}
                    </span>
                  )}
                </button>

                {/* Profile Menu */}
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                    {candidateData?.first_name?.[0]}
                  </div>
                  <span className="hidden sm:block text-sm font-medium text-gray-700">
                    {candidateData?.first_name}
                  </span>
                </div>
              </div>
            </div>
          </header>

          {/* Content Area */}
          <div className="flex-1 p-4 lg:p-6 overflow-auto">
            {activeTab === 'dashboard' && <DashboardTab />}
            {activeTab === 'profile' && <ProfileTab />}
            {activeTab === 'applications' && <ApplicationsTab />}
            {activeTab === 'interviews' && <InterviewsTab />}
            {activeTab === 'documents' && <DocumentsTab />}
            {activeTab === 'messages' && <MessagesTab />}
            {activeTab === 'feedback' && <FeedbackTab />}
            {activeTab === 'settings' && <SettingsTab />}
          </div>
        </main>
      </div>
    </CandidateContext.Provider>
  );
};

// Dashboard Tab Component
const DashboardTab = () => {
  const { dashboardData } = useContext(CandidateContext);
  
  return (
    <div className="space-y-6">
      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Briefcase className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Applications</p>
              <p className="text-2xl font-bold text-gray-900">{dashboardData?.active_applications?.length || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <Calendar className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Upcoming Interviews</p>
              <p className="text-2xl font-bold text-gray-900">{dashboardData?.upcoming_interviews?.length || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pending Tasks</p>
              <p className="text-2xl font-bold text-gray-900">{dashboardData?.pending_tasks?.length || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <TrendingUp className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Profile Score</p>
              <p className="text-2xl font-bold text-gray-900">{dashboardData?.profile_completion || 0}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Dashboard Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Upcoming Interviews */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Upcoming Interviews</h3>
            </div>
            <div className="p-6">
              {dashboardData?.upcoming_interviews?.length > 0 ? (
                <div className="space-y-4">
                  {dashboardData.upcoming_interviews.map((interview, index) => {
                    const { date, time } = formatDateTime(interview.scheduled_time);
                    return (
                      <div key={index} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                        <div className="flex-shrink-0">
                          <Calendar className="h-8 w-8 text-purple-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">
                            {interview.interview_type.charAt(0).toUpperCase() + interview.interview_type.slice(1)} Interview
                          </p>
                          <p className="text-sm text-gray-600">{interview.job_title}</p>
                          <p className="text-xs text-gray-500">
                            {date} at {time} • {interview.interviewer_name}
                          </p>
                        </div>
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">No upcoming interviews</p>
                  <p className="text-sm text-gray-500">Your scheduled interviews will appear here</p>
                </div>
              )}
            </div>
          </div>

          {/* Active Applications */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Application Progress</h3>
            </div>
            <div className="p-6">
              {dashboardData?.active_applications?.length > 0 ? (
                <div className="space-y-4">
                  {dashboardData.active_applications.map((app, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-medium text-gray-900">{app.job_title}</h4>
                          <p className="text-sm text-gray-600">{app.company}</p>
                        </div>
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                          {app.current_stage}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${getProgressColor(app.progress_percentage)}`}
                          style={{ width: `${app.progress_percentage}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>Progress</span>
                        <span>{app.progress_percentage}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Briefcase className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">No active applications</p>
                  <p className="text-sm text-gray-500">Applied positions will appear here</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Pending Tasks */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Pending Tasks</h3>
            </div>
            <div className="p-6">
              {dashboardData?.pending_tasks?.length > 0 ? (
                <div className="space-y-3">
                  {dashboardData.pending_tasks.map((task, index) => {
                    const { date, time } = formatDateTime(task.deadline);
                    return (
                      <div key={index} className="flex items-start space-x-3 p-3 border border-gray-200 rounded-lg">
                        <AlertCircle className={`h-5 w-5 mt-0.5 ${
                          task.priority === 'high' ? 'text-red-500' : 
                          task.priority === 'medium' ? 'text-yellow-500' : 'text-blue-500'
                        }`} />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{task.title}</p>
                          <p className="text-xs text-gray-500">Due: {date} {time}</p>
                          <span className={`inline-block mt-2 px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(task.priority)}`}>
                            {task.priority} priority
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-3" />
                  <p className="text-gray-600">All caught up!</p>
                  <p className="text-sm text-gray-500">No pending tasks</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Placeholder components for other tabs
const ProfileTab = () => <div className="text-center py-12"><User className="h-16 w-16 text-gray-400 mx-auto mb-4" /><p className="text-gray-600">Profile management coming soon...</p></div>;
const ApplicationsTab = () => <div className="text-center py-12"><Briefcase className="h-16 w-16 text-gray-400 mx-auto mb-4" /><p className="text-gray-600">Applications view coming soon...</p></div>;
const InterviewsTab = () => <div className="text-center py-12"><Calendar className="h-16 w-16 text-gray-400 mx-auto mb-4" /><p className="text-gray-600">Interviews management coming soon...</p></div>;
const DocumentsTab = () => <div className="text-center py-12"><FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" /><p className="text-gray-600">Document management coming soon...</p></div>;
const MessagesTab = () => <div className="text-center py-12"><MessageCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" /><p className="text-gray-600">Messaging system coming soon...</p></div>;
const FeedbackTab = () => <div className="text-center py-12"><Star className="h-16 w-16 text-gray-400 mx-auto mb-4" /><p className="text-gray-600">Feedback system coming soon...</p></div>;
const SettingsTab = () => <div className="text-center py-12"><Settings className="h-16 w-16 text-gray-400 mx-auto mb-4" /><p className="text-gray-600">Settings management coming soon...</p></div>;

// Helper function (already defined in component above, but separated for clarity)
const formatDateTime = (dateTimeString) => {
  const date = new Date(dateTimeString);
  return {
    date: date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    }),
    time: date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    })
  };
};

const getProgressColor = (percentage) => {
  if (percentage >= 80) return 'bg-green-500';
  if (percentage >= 60) return 'bg-blue-500';
  if (percentage >= 40) return 'bg-yellow-500';
  return 'bg-red-500';
};

const getPriorityColor = (priority) => {
  switch (priority) {
    case 'high': return 'text-red-600 bg-red-100';
    case 'medium': return 'text-yellow-600 bg-yellow-100';
    case 'low': return 'text-green-600 bg-green-100';
    default: return 'text-gray-600 bg-gray-100';
  }
};

export default CandidatePortal;